"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SESSION_SCREEN_OUTCOMES = exports.voidSessionSchema = exports.resumeSessionSchema = exports.pauseSessionSchema = exports.stopSessionSchema = exports.startSessionSchema = exports.TAB_STATUSES = exports.SESSION_END_REASONS = exports.SESSION_STATUSES = void 0;
const zod_1 = require("zod");
/**
 * Tabs & sessions — SPEC §5.3.
 *
 * A Tab is a named party. It groups MANY device sessions plus orders, which is
 * what makes "12 friends on 12 PCs under one name, one long receipt at the end"
 * work. Reserving a device therefore either opens a NEW tab under a customer
 * name, or attaches the device to an EXISTING open tab.
 *
 * Money crosses the wire as decimal strings of millidinars — see `devices.ts`.
 */
exports.SESSION_STATUSES = ['ACTIVE', 'PAUSED', 'CLOSED', 'VOID'];
/**
 * Why a sitting closed, recorded only when the close was not an ordinary one —
 * the Prisma enum `SessionEndReason` on `Session.endedReason` (owner,
 * 2026-10-14). Null on every Stop, Sign out, time-up and out-of-credit close,
 * and on every sitting closed before the column existed.
 *
 * - `POWERED_OFF` — the PC was turned off under the sitting: Windows told
 *   PortalApp it was shutting down, or the agent and the portal were both
 *   silent for 10 minutes. Billed up to that moment and closed by the built-in
 *   Portal actor. The staff app says "PC turned off" wherever the closed
 *   sitting's end is shown.
 */
exports.SESSION_END_REASONS = ['POWERED_OFF'];
exports.TAB_STATUSES = ['OPEN', 'CLOSED', 'MERGED'];
/* -------------------------------------------------------------------------- */
/* Start a session (reserve a device)                                         */
/* -------------------------------------------------------------------------- */
exports.startSessionSchema = zod_1.z
    .object({
    deviceId: zod_1.z.string().min(1),
    /**
     * Open a new tab under this walk-in name. Mutually exclusive with `tabId`.
     * SPEC §5.6 calls this the "Visitor", as opposed to a registered Customer.
     */
    customerName: zod_1.z.string().trim().min(1, 'Customer name is required.').max(120).optional(),
    /** Attach this device to an already-open tab — the 12-PCs-one-name case. */
    tabId: zod_1.z.string().min(1).optional(),
    /** Registered account, when the party is a known customer (SPEC §5.5). */
    customerAccountId: zod_1.z.string().min(1).optional(),
    /**
     * Prepaid/booked minutes. Sets the session's planned end, which drives the
     * "About to end" tile state and the agent's low-time warning. Omit for
     * open-ended play billed on exit.
     */
    plannedMinutes: zod_1.z
        .number()
        .int()
        .min(1)
        .max(24 * 60)
        .optional(),
    /**
     * Free MODE, not free of charge (owner: "free mode is billed as well …
     * the free means that they can roam the windows entirely"): the sitting
     * gets the Windows desktop instead of the portal and BILLS AT THE NORMAL
     * RATE — see the corrective note in `session-cost.ts`, which used to
     * short-circuit here and no longer does. Omitted means "whatever the
     * device's own freeModeEnabled says"; an explicit value overrides per
     * sitting.
     */
    freeMode: zod_1.z.boolean().optional(),
    /**
     * Seat this sitting on a Boot Camp day pass the party already bought
     * (SPEC §6.1). The server checks the pass is still valid and belongs to
     * this party, then snapshots a rate of ZERO onto the session — so the
     * sitting bills nothing without any billing code knowing passes exist.
     *
     * Distinct from `freeMode`, which is about the SCREEN (Windows vs the
     * portal) and bills normally; a pass is time the customer has already
     * been charged for, and refusing to seat it would mean selling something
     * that cannot be used. They also have to stay apart in the §5.7 reports,
     * where "sold up front" is a different fact about a zero-rated sitting.
     */
    dayPassId: zod_1.z.string().min(1).optional(),
    /**
     * The staff Play button (owner, 2026-08-12: "the Play function is only
     * used when someone sits and a staff member opens the PC for them for
     * free play"): a COURTESY sitting. The server snapshots a rate of ZERO —
     * it bills nothing however long it runs — and the Outstanding screen
     * skips it entirely. Gated on `discounts.apply`, the key the Play button
     * already hides behind: giving the room away is a privilege.
     *
     * The third zero-rated thing, kept apart from the other two on purpose:
     * `freeMode` is about the SCREEN and bills normally; a day pass is play
     * SOLD up front. This is play GIVEN away, and §5.7's reader must be able
     * to say which of the three priced any zero-rated sitting.
     */
    complimentary: zod_1.z.boolean().optional(),
})
    .refine((value) => !(value.dayPassId && value.freeMode === true), {
    // A pass snapshots a ZERO rate while free mode bills normally — combined,
    // the pass would be consumed on a sitting whose rate it did not set, and
    // no reader of the bill could say which rule priced it.
    message: 'A sitting is either on a day pass or in free mode, not both.',
    path: ['dayPassId'],
})
    .refine((value) => !(value.dayPassId && value.complimentary === true), {
    // Comping a sitting the customer already paid for would silently burn
    // their pass on play the venue meant to give away.
    message: 'A sitting is either on a day pass or complimentary, not both.',
    path: ['dayPassId'],
})
    .refine((value) => Boolean(value.customerName ?? value.tabId ?? value.customerAccountId), {
    message: 'Give a customer name, or pick an open tab.',
    path: ['customerName'],
})
    .refine((value) => !(value.customerName && value.tabId), {
    message: 'Choose either a new name or an existing tab, not both.',
    path: ['tabId'],
});
/* -------------------------------------------------------------------------- */
/* Stop a session                                                             */
/* -------------------------------------------------------------------------- */
exports.stopSessionSchema = zod_1.z.object({
    /**
     * Rounds played, for ROUND-billed devices (billiard, console matches).
     * Ignored by hourly devices, which bill prorated time.
     */
    singleRounds: zod_1.z.number().int().min(0).max(999).default(0),
    doubleRounds: zod_1.z.number().int().min(0).max(999).default(0),
    threeBallRounds: zod_1.z.number().int().min(0).max(999).default(0),
    /** Discount in whole IQD, applied before the venue rounding rule. */
    discountIqd: zod_1.z.number().int().min(0).default(0),
});
/* -------------------------------------------------------------------------- */
/* Pause / resume (wave 3)                                                    */
/* -------------------------------------------------------------------------- */
/**
 * Pause stops the clock without closing the sitting: the paused stretch is
 * excluded from billing (`pausedSeconds`, which `billableMilliseconds`
 * already honours) and recorded as a `SessionPause` row for the §5.7
 * activity report. Both bodies carry only an optional reason — which session
 * is in the URL, and there is nothing else an honest pause needs.
 */
exports.pauseSessionSchema = zod_1.z.object({
    reason: zod_1.z.string().trim().max(200).optional(),
});
exports.resumeSessionSchema = zod_1.z.object({}).strict();
/* -------------------------------------------------------------------------- */
/* Void (SPEC §5.3 — `sessions.void`)                                          */
/* -------------------------------------------------------------------------- */
/**
 * Void a sitting: it was opened by mistake, on the wrong device, or for
 * someone who never sat down. The session ends as VOID and bills NOTHING —
 * which is exactly why it is its own privilege (`sessions.void`), separate
 * from closing one, and audited: a void is the one way play time leaves the
 * system without money.
 *
 * A reason is mandatory and must say something — three characters is the
 * floor that refuses "x" and "." without refusing "dup" or "test".
 *
 * Route: `POST /sessions/:id/void`.
 */
exports.voidSessionSchema = zod_1.z.object({
    reason: zod_1.z.string().trim().min(3, 'Say why this session is being voided.').max(300),
});
/**
 * Whether the machine itself heard about a pause or resume.
 *
 * Pausing does TWO things — it stops the clock and it seals the screen — and
 * they can succeed independently. The clock is a money decision staff have
 * already made, so it is never blocked on the PC being reachable; a session
 * that stayed billable because a network switch was down would charge a
 * customer for time the venue decided not to bill. But staff must be told when
 * the screen half did not land, or they walk away from a machine they believe
 * is sealed and the customer keeps playing.
 *
 * - `sent` — the frame reached that PC's agent.
 * - `offline` — no agent connected. The clock stopped; the screen did not.
 * - `unsupported` — the agent is an older build with no pause handling. Same
 *   consequence, different fix: that PC needs the current kiosk package.
 * - `no_agent` — nothing was ever paired here (a pool table, a cafe seat).
 *   Not a fault; there is no screen to seal.
 */
exports.SESSION_SCREEN_OUTCOMES = ['sent', 'offline', 'unsupported', 'no_agent'];
//# sourceMappingURL=sessions.js.map